/* eslint-disable prefer-destructuring */
import {findIndex} from 'lodash';

import {PDFPageView} from './page_view';
import {
  UNKNOWN_SCALE,
  SCROLLBAR_PADDING,
  VERTICAL_PADDING,
  MAX_AUTO_SCALE,
  CSS_UNITS,
  DEFAULT_SCALE,
  DEFAULT_SCALE_VALUE,
  DEFAULT_CACHE_SIZE,
} from './pdf_viewer_constants';
import pdf from './pdfjs_loader';
import {RenderingStates, RenderQueue} from './render_queue';

/* eslint-disable no-param-reassign */
function PDFPageViewBuffer(size) {
  const data = [];
  this.push = (view) => {
    const i = data.indexOf(view);
    if (i >= 0) {
      data.splice(i, 1);
    }
    data.push(view);
    if (data.length > size) {
      data.shift().destroy();
    }
  };
  this.resize = (newSize) => {
    size = newSize;
    while (data.length > size) {
      data.shift().destroy();
    }
  };
}

/* eslint-enable no-param-reassign */

function isSameScale(oldScale, newScale) {
  if (newScale === oldScale) {
    return true;
  }
  // Prevent unnecessary re-rendering of all pages when the scale
  // changes only because of limited numerical precision.
  return Math.abs(newScale - oldScale) < 1e-15;
}

/**
 * TODO refactor to controller
 */
function watchScroll(viewAreaElement, callback) {
  let state;
  let rAF;
  const debounceScroll = (/* evt */) => {
    if (rAF) {
      return;
    }

    // schedule an invocation of scroll for next animation frame.
    rAF = window.requestAnimationFrame(() => {
      rAF = null;

      const currentY = viewAreaElement.scrollTop;
      const {lastY} = state;
      if (currentY !== lastY) {
        state.down = currentY > lastY;
      }
      state.lastY = currentY;
      callback(state);
    });
  };

  state = {
    down: true,
    lastY: viewAreaElement.scrollTop,
    _eventHandler: debounceScroll,
  };

  rAF = null;
  viewAreaElement.addEventListener('scroll', debounceScroll, true);
  return state;
}

export class PDFViewer {
  constructor(options) {
    this.container = options.container;
    this.viewer = options.viewer || options.container.firstElementChild;
    this.eventBus = options.eventBus;
    this.linkService = options.linkService;
    this.downloadManager = options.downloadManager || null;
    this.removePageBorders = options.removePageBorders || false;
    this.TCLayerBuilder = options.TCLayerBuilder;

    this.defaultRenderingQueue = !options.renderingQueue;
    if (this.defaultRenderingQueue) {
      // Custom rendering queue is not specified, using default one
      this.renderingQueue = new RenderQueue();
      this.renderingQueue.setViewer(this);
    } else {
      this.renderingQueue = options.renderingQueue;
    }

    this.scroll = watchScroll(this.container, this._scrollUpdate.bind(this));

    this._resetView();

    if (this.removePageBorders) {
      this.viewer.classList.add('removePageBorders');
    }
  }

  getPagesCount() {
    return this._pages.length;
  }

  getPageView(index) {
    return this._pages[index];
  }

  getPageNumber() {
    return this._currentPageNumber;
  }

  setPageNumber(val) {
    if (!this.pdfDocument) {
      this._currentPageNumber = val;
      return;
    }
    this._setCurrentPageNumber(val);
    // The intent can be to just reset a scroll position and/or scale.
    this._resetCurrentPageView();
  }

  getScale() {
    return this._currentScale !== UNKNOWN_SCALE
      ? this._currentScale
      : DEFAULT_SCALE;
  }

  /**
   * Alias page count
   */
  get pagesCount() {
    return this._pages.length;
  }

  /**
   * @param {number} val - Scale of the pages in percents.
   */
  setScale(val) {
    if (Number.isNaN(val)) {
      throw new Error('Invalid numeric scale');
    }
    if (!this.pdfDocument) {
      this._currentScale = val;
      this._currentScaleValue = val !== UNKNOWN_SCALE ? val.toString() : null;
      return;
    }
    this._setScale(val, false);
  }

  /**
   * @returns {string}
   */
  getZoom() {
    return this._currentScaleValue;
  }

  /**
   * @param val - The scale of the pages (in percent or predefined value).
   */
  setZoom(val) {
    if (!this.pdfDocument) {
      this._currentScale = Number.isNaN(val) ? UNKNOWN_SCALE : val;
      this._currentScaleValue = val;
      return;
    }
    this._setScale(val, false);
    this.update();
  }

  /**
   * @returns {number}
   */
  getRotation() {
    return this._pagesRotation;
  }

  /**
   * @param {number} rotation - The rotation of the pages (0, 90, 180, 270).
   */
  setRotation(rotation) {
    this._pagesRotation = rotation;

    for (let i = 0, len = this._pages.length; i < len; i++) {
      const pageView = this._pages[i];
      pageView.update(pageView.scale, rotation);
    }

    this._setScale(this._currentScaleValue, true);
    this.update();
  }

  get isHorizontalScrollbarEnabled() {
    return this.container.scrollWidth > this.container.clientWidth;
  }

  _setCurrentPageNumber(val) {
    let arg;

    if (this._currentPageNumber === val) {
      return;
    }
    if (!(val > 0 && val <= this.pagesCount)) {
      arg = {
        source: this,
        pageNumber: this._currentPageNumber,
        previousPageNumber: val,
      };
      this.eventBus.dispatch('pagechanging', arg);
      this.eventBus.dispatch('pagechange', arg);
      return;
    }

    arg = {
      source: this,
      pageNumber: val,
      previousPageNumber: this._currentPageNumber,
    };
    this._currentPageNumber = val;
    this.eventBus.dispatch('pagechanging', arg);
    this.eventBus.dispatch('pagechange', arg);
  }

  /**
   * @param pdfDocument {PDFDocument}
   */
  setDocument(pdfDocument) {
    const self = this;
    let resolvePagesPromise;
    let isOnePageRenderedResolved = false;
    let resolveOnePageRendered = null;

    if (this.pdfDocument) {
      this._resetView();
    }
    this.pdfDocument = pdfDocument;
    if (!pdfDocument) {
      return Promise.resolve(null);
    }

    const pagesCount = pdfDocument.numPages;

    const pagesPromise = new Promise((resolve) => {
      resolvePagesPromise = resolve;
    });
    this.pagesPromise = pagesPromise;
    pagesPromise.then(() => {
      this.eventBus.dispatch('pagesloaded', {
        source: this,
        pagesCount,
      });
    });

    const onePageRendered = new Promise((resolve) => {
      resolveOnePageRendered = resolve;
    });

    this.onePageRendered = onePageRendered;

    /* eslint-disable no-param-reassign */
    function bindOnAfterAndBeforeDraw(pageView) {
      pageView.onBeforeDraw = () => {
        // Add the page to the buffer at the start of drawing. That way it can
        // be evicted from the buffer and destroyed even if we pause its
        // rendering.
        self._buffer.push(this);
      };

      // when page is painted, using the image as thumbnail base
      pageView.onAfterDraw = () => {
        if (!isOnePageRenderedResolved) {
          isOnePageRenderedResolved = true;
          resolveOnePageRendered();
        }
      };
    }

    /* eslint-enable no-param-reassign */

    const firstPagePromise = pdfDocument.getPage(1);
    this.firstPagePromise = firstPagePromise;

    // Fetch a single page so we can get a viewport that will be the default
    // viewport for all pages
    const result = firstPagePromise.then((pdfPage) => {
      let pageNum;
      const scale = this.currentScale;
      const viewport = pdfPage.getViewport({scale: scale * CSS_UNITS});

      for (pageNum = 1; pageNum <= pagesCount; ++pageNum) {
        const pageView = new PDFPageView({
          container: this.viewer,
          eventBus: this.eventBus,
          id: pageNum,
          scale,
          defaultViewport: viewport.clone(),
          TCLayerBuilder: this.TCLayerBuilder,
          renderingQueue: this.renderingQueue,
        });
        bindOnAfterAndBeforeDraw(pageView);
        this._pages.push(pageView);
      }

      const {linkService} = this;

      // Fetch all the pages since the viewport is needed before printing
      // starts to create the correct size canvas. Wait until one page is
      // rendered so we don't tie up too many resources early on.
      onePageRendered.then(() => {
        let getPagesLeft;

        const getPageResolved = (pageNum_, pdfPage_) => {
          const currPageView = this._pages[pageNum_ - 1];
          if (!currPageView.pdfPage) {
            currPageView.setPdfPage(pdfPage_);
          }
          linkService.cachePageRef(pageNum_, pdfPage_.ref);
          getPagesLeft -= 1;
          if (!getPagesLeft) {
            resolvePagesPromise();
          }
        };

        if (!pdf.disableAutoFetch) {
          getPagesLeft = pagesCount;
          for (let currPageNum = 1; currPageNum <= pagesCount; ++currPageNum) {
            pdfDocument
              .getPage(currPageNum)
              .then(getPageResolved.bind(null, currPageNum));
          }
        } else {
          resolvePagesPromise();
        }
      });
      this.eventBus.dispatch('pagesinit', {source: this});

      if (this.defaultRenderingQueue) {
        this.update();
      }
    });

    return result;
  }

  _resetView() {
    this._pages = [];
    this._currentPageNumber = 1;
    this._currentScale = UNKNOWN_SCALE;
    this._currentScaleValue = null;
    this._buffer = new PDFPageViewBuffer(DEFAULT_CACHE_SIZE);
    this._location = null;
    this._pagesRotation = 0;
    this._pagesRequests = [];

    const container = this.viewer;
    while (container.hasChildNodes()) {
      container.removeChild(container.lastChild);
    }
  }

  _scrollUpdate() {
    if (this.pagesCount === 0) {
      return;
    }

    this.update();
    for (let i = 0, len = this._pages.length; i < len; i++) {
      this._pages[i].updatePosition();
    }
  }

  _setScaleDispatchEvent(newScale, newValue, preset) {
    const arg = {
      source: this,
      scale: newScale,
      presetValue: preset ? newValue : undefined,
    };

    this.eventBus.dispatch('scalechanging', arg);
    this.eventBus.dispatch('scalechange', arg);
  }

  _setScaleUpdatePages(newScale, newValue, noScroll, preset) {
    this._currentScaleValue = newValue;

    if (isSameScale(this._currentScale, newScale)) {
      if (preset) {
        this._setScaleDispatchEvent(newScale, newValue, true);
      }
      return;
    }

    for (let i = 0, len = this._pages.length; i < len; i++) {
      this._pages[i].update(newScale);
    }
    this._currentScale = newScale;

    if (!noScroll) {
      let page = this._currentPageNumber;
      let dest;
      // !! check this
      if (
        this._location &&
        !pdf.ignoreCurrentPositionOnZoom &&
        !(this.isChangingPresentationMode || false)
      ) {
        page = this._location.pageNumber;
        dest = [
          null,
          {name: 'XYZ'},
          this._location.left,
          this._location.top,
          null,
        ];
      } else {
        dest = [null, {name: 'XYZ'}, 0, 0, null];
      }
      this.scrollPageIntoView(page, dest);
    }

    this._setScaleDispatchEvent(newScale, newValue, preset);

    if (this.defaultRenderingQueue) {
      this.update();
    }
  }

  _setScale(value, noScroll, force) {
    let scale = parseFloat(value);

    if (scale > 0) {
      this._setScaleUpdatePages(scale, value, noScroll, false);
    } else {
      const currentPage = this._pages[this._currentPageNumber - 1];
      if (!currentPage && !force) {
        return;
      }
      const hPadding = this.removePageBorders || false ? 0 : SCROLLBAR_PADDING;
      const vPadding = this.removePageBorders || false ? 0 : VERTICAL_PADDING;
      const pageWidthScale =
        ((this.container.clientWidth - hPadding) / currentPage.width()) *
        currentPage.scale;
      const pageHeightScale =
        ((this.container.clientHeight - vPadding) / currentPage.height()) *
        currentPage.scale;
      let isLandscape;
      let horizontalScale;
      switch (value) {
        case 'page-actual':
          scale = 1;
          break;
        case 'page-width':
          scale = pageWidthScale;
          break;
        case 'page-height':
          scale = pageHeightScale;
          break;
        case 'page-fit':
          scale = Math.min(pageWidthScale, pageHeightScale);
          break;
        case 'auto':
          isLandscape = currentPage.width > currentPage.height;

          // For pages in landscape mode, fit the page height to the viewer
          // *unless* the page would thus become too wide to fit horizontally.
          horizontalScale = isLandscape
            ? Math.min(pageHeightScale, pageWidthScale)
            : pageWidthScale;
          scale = Math.min(MAX_AUTO_SCALE, horizontalScale);
          break;
        default:
          scale = 1;
          return;
      }
      this._setScaleUpdatePages(scale, value, noScroll, true);
    }
  }

  /**
   * Refreshes page view: scrolls to the current page and updates the scale.
   */
  _resetCurrentPageView() {
    const pageView = this._pages[this._currentPageNumber - 1];
    PDFViewer.scrollIntoView(pageView.div);
  }

  static scrollIntoView(element, spot, skipOverflowHiddenElements) {
    // Assuming offsetParent is available (it's not available when viewer is in
    // hidden iframe or object). We have to scroll: if the offsetParent is not set
    // producing the error. See also animationStartedClosure.
    let parent = element.offsetParent;
    if (!parent) {
      return;
    }
    const checkOverflow = skipOverflowHiddenElements || false;
    let offsetY = element.offsetTop + element.clientTop;
    let offsetX = element.offsetLeft + element.clientLeft;
    while (
      parent.clientHeight === parent.scrollHeight ||
      (checkOverflow && getComputedStyle(parent).overflow === 'hidden')
    ) {
      if (parent.dataset._scaleY) {
        offsetY /= parent.dataset._scaleY;
        offsetX /= parent.dataset._scaleX;
      }
      offsetY += parent.offsetTop;
      offsetX += parent.offsetLeft;
      parent = parent.offsetParent;
      if (!parent) {
        return; // no need to scroll
      }
    }
    if (spot) {
      if (spot.top !== undefined) {
        offsetY += spot.top;
      }
      if (spot.left !== undefined) {
        offsetX += spot.left;
        parent.scrollLeft = offsetX;
      }
    }
    parent.scrollTop = offsetY;
  }

  /**
   * Scrolls page into view.
   * @param {number} pageNumber
   * @param {Array} dest - (optional) original PDF destination array:
   *   <page-ref> </XYZ|FitXXX> <args..>
   */
  scrollPageIntoView(pageNumber, dest) {
    let x = 0;
    let y = 0;
    let width = 0;
    let height = 0;
    let scale = 0;

    if (!this.pdfDocument) {
      return;
    }

    const pageView = this._pages[pageNumber - 1];
    const changeOrientation = !(pageView.rotation % 180 === 0);
    // TODO remove redundant func. apply
    const pageWidth =
      (changeOrientation ? pageView.height() : pageView.width()) /
      pageView.scale /
      CSS_UNITS;
    const pageHeight =
      (changeOrientation ? pageView.width() : pageView.height()) /
      pageView.scale /
      CSS_UNITS;

    let hPadding;
    let vPadding;
    let widthScale;
    let heightScale;
    switch (dest[1].name) {
      case 'XYZ':
        x = dest[2];
        y = dest[3];
        scale = dest[4];
        // If x and/or y coordinates are not supplied, default to
        // _top_ left of the page (not the obvious bottom left,
        // since aligning the bottom of the intended page with the
        // top of the window is rarely helpful).
        x = x !== null ? x : 0;
        y = y !== null ? y : pageHeight;
        break;
      case 'Fit':
      case 'FitB':
        scale = 'page-fit';
        break;
      case 'FitH':
      case 'FitBH':
        y = dest[2];
        scale = 'page-width';
        // According to the PDF spec, section 12.3.2.2, a `null` value in the
        // parameter should maintain the position relative to the new page.
        if (y === null && this._location) {
          x = this._location.left;
          y = this._location.top;
        }
        break;
      case 'FitV':
      case 'FitBV':
        x = dest[2];
        width = pageWidth;
        height = pageHeight;
        scale = 'page-height';
        break;
      case 'FitR':
        x = dest[2];
        y = dest[3];
        width = dest[4] - x;
        height = dest[5] - y;
        hPadding = this.removePageBorders ? 0 : SCROLLBAR_PADDING;
        vPadding = this.removePageBorders ? 0 : VERTICAL_PADDING;

        widthScale =
          (this.container.clientWidth - hPadding) / width / CSS_UNITS;
        heightScale =
          (this.container.clientHeight - vPadding) / height / CSS_UNITS;
        scale = Math.min(Math.abs(widthScale), Math.abs(heightScale));
        break;
      default:
        return;
    }

    if (scale && scale !== this._currentScale) {
      this.currentScaleValue = scale;
    } else if (this._currentScale === UNKNOWN_SCALE) {
      this.currentScaleValue = DEFAULT_SCALE_VALUE;
    }

    if (scale === 'page-fit' && !dest[4]) {
      PDFViewer.scrollIntoView(pageView.div);
      return;
    }

    const boundingRect = [
      pageView.viewport.convertToViewportPoint(x, y),
      pageView.viewport.convertToViewportPoint(x + width, y + height),
    ];
    const left = Math.min(boundingRect[0][0], boundingRect[1][0]);
    const top = Math.min(boundingRect[0][1], boundingRect[1][1]);

    PDFViewer.scrollIntoView(pageView.div, {left, top});
  }

  _updateLocation(firstPage) {
    const currentScale = this._currentScale;
    const currentScaleValue = this._currentScaleValue;
    const normalizedScaleValue =
      parseFloat(currentScaleValue) === currentScale
        ? Math.round(currentScale * 10000) / 100
        : currentScaleValue;
    const pageNumber = firstPage.id;
    let pdfOpenParams = `#page=${pageNumber}`;

    pdfOpenParams += `&zoom=${normalizedScaleValue}`;
    const currentPageView = this._pages[pageNumber - 1];
    const {container} = this;
    const topLeft = currentPageView.getPagePoint(
      container.scrollLeft - firstPage.x,
      container.scrollTop - firstPage.y
    );
    const intLeft = Math.round(topLeft[0]);
    const intTop = Math.round(topLeft[1]);
    pdfOpenParams += `,${intLeft},${intTop}`;

    this._location = {
      pageNumber,
      scale: normalizedScaleValue,
      top: intTop,
      left: intLeft,
      pdfOpenParams,
    };
  }

  updateAfterResize() {
    this._setScale(this.currentScaleValue, true, true);
  }

  update() {
    const visible = this._getVisiblePages();
    const visiblePages = visible.views;

    if (visiblePages.length === 0) {
      return;
    }

    const suggestedCacheSize = Math.max(
      DEFAULT_CACHE_SIZE,
      2 * visiblePages.length + 1
    );
    this._buffer.resize(suggestedCacheSize);

    this.renderingQueue.renderHighestPriority(visible);

    let currentId = this._currentPageNumber;
    const firstPage = visible.first;
    let stillFullyVisible = false;

    for (let i = 0, len = visiblePages.length; i < len; ++i) {
      const page = visiblePages[i];

      if (page.percent < 100) {
        break;
      }
      if (page.id === currentId) {
        stillFullyVisible = true;
        break;
      }
    }

    if (!stillFullyVisible) {
      currentId = visiblePages[0].id;
    }

    this._setCurrentPageNumber(currentId);
    this._updateLocation(firstPage);

    this.eventBus.dispatch('updateviewarea', {
      source: this,
      location: this._location,
    });
  }

  containsElement(element) {
    return this.container.contains(element);
  }

  focus() {
    this.container.focus();
  }

  _getVisiblePages() {
    return this.getVisibleElements(this.container, this._pages, true);
  }

  /**
   * TODO move to controller, pass container to constructor
   */
  // eslint-disable-next-line class-methods-use-this
  getVisibleElements(_scrollEl, views, sortByVisibility) {
    // TODO performance fix
    // const scrollEl = $(_scrollEl).find('.container-pdf__viewer-container')[0];
    const scrollEl = _scrollEl.querySelector(
      '.container-pdf__viewer-container'
    );
    const top = scrollEl.scrollTop;
    const bottom = top + scrollEl.clientHeight;
    const left = scrollEl.scrollLeft;
    const right = left + scrollEl.clientWidth;
    const visible = [];

    const firstVisibleElementInd =
      views.length === 0
        ? 0
        : findIndex(views, (currView) => {
            if (typeof currView.div === 'undefined') {
              // element was stolen
              return false;
            }
            const currElement = currView.div;
            const elementBottom =
              currElement.offsetTop +
              currElement.clientTop +
              currElement.clientHeight;
            return elementBottom > top;
          });

    for (let i = firstVisibleElementInd, len = views.length; i < len; i++) {
      const view = views[i];
      if (typeof view !== 'object' || typeof view.div === 'undefined') {
        // element was stolen
        // eslint-disable-next-line no-continue
        continue;
      }
      const element = view.div;
      const currentHeight = element.offsetTop + element.clientTop;
      const viewHeight = element.clientHeight;

      if (currentHeight > bottom) {
        break;
      }

      const currentWidth = element.offsetLeft + element.clientLeft;
      const viewWidth = element.clientWidth;
      if (currentWidth + viewWidth < left || currentWidth > right) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const hiddenHeight =
        Math.max(0, top - currentHeight) +
        Math.max(0, currentHeight + viewHeight - bottom);
      const percentHeight =
        // eslint-disable-next-line no-bitwise
        (((viewHeight - hiddenHeight) * 100) / viewHeight) | 0;

      visible.push({
        id: view.id,
        x: currentWidth,
        y: currentHeight,
        view,
        percent: percentHeight,
      });
    }

    const first = visible[0];
    const last = visible[visible.length - 1];

    if (sortByVisibility) {
      visible.sort((a, b) => {
        const pc = a.percent - b.percent;
        if (Math.abs(pc) > 0.001) {
          return -pc;
        }
        return a.id - b.id; // ensure stability
      });
    }
    return {first, last, views: visible};
  }

  cleanup() {
    for (let i = 0, ii = this._pages.length; i < ii; i++) {
      if (
        this._pages[i] &&
        this._pages[i].renderingState !== RenderingStates.FINISHED
      ) {
        this._pages[i].reset();
      }
    }
  }

  /**
   * @param {PDFPageView} pageView
   * @returns {Object}
   * @private
   */
  _ensurePdfPageLoaded(pageView) {
    if (pageView.pdfPage) {
      return Promise.resolve(pageView.pdfPage);
    }
    const pageNumber = pageView.id;
    if (this._pagesRequests[pageNumber]) {
      return this._pagesRequests[pageNumber];
    }
    const promise = this.pdfDocument.getPage(pageNumber).then((pdfPage) => {
      pageView.setPdfPage(pdfPage);
      this._pagesRequests[pageNumber] = null;
      return pdfPage;
    });
    this._pagesRequests[pageNumber] = promise;
    return promise;
  }

  forceRendering(currentlyVisiblePages) {
    const visiblePages = currentlyVisiblePages || this._getVisiblePages();
    const pageView = RenderQueue.getHighestPriority(
      visiblePages,
      this._pages,
      this.scroll.down
    );
    if (pageView) {
      this._ensurePdfPageLoaded(pageView).then(() => {
        this.renderingQueue.renderView(pageView);
      });
      return true;
    }
    return false;
  }

  getPageTextContent(pageIndex) {
    return this.pdfDocument
      .getPage(pageIndex + 1)
      .then((page) => page.getTextContent({normalizeWhitespace: true}));
  }

  /* eslint-disable no-unused-vars */
  /**
   * @param {HTMLDivElement} textLayerDiv
   * @param {number} pageIndex
   * @param {PageViewport} viewport
   * @returns {TextLayerBuilder}
   */
  // eslint-disable-next-line class-methods-use-this
  createTextLayerBuilder(textLayerDiv, pageIndex, viewport) {}

  /* eslint-enable no-unused-vars */

  setFindController(findController) {
    this.findController = findController;
  }
}

export default PDFViewer;
